import { lerPagina } from '@titularis/ui';
import type { EstadoDoArtigo, OrdemDosArtigos, ResumoDosArtigos } from '@titularis/api-client';
import { api } from '../../api';
import { lerOpcao, ORDENS } from './ordens';

/**
 * O recorte do inventário de comentários: o que filtra, em que ordem e que
 * página.
 *
 * **Mora fora da tela pelo mesmo motivo da lista do Comentário Assistido:** o
 * recorte é lido pela lista e pelo resumo, e duas leituras divergiriam no
 * primeiro filtro novo. A divergência apareceria como a contagem do `Segmented`
 * prometendo linhas que a tabela não mostra - o pior tipo de defeito, porque não
 * levanta erro nenhum.
 */

export const TAMANHOS_DE_PAGINA = [20, 50, 100];

/*
 * Vinte por página: esta tela é de **procura** - alguém vem achar um comentário
 * para corrigir -, e não de varredura. Quem quer ver a lei inteira tem a tela da
 * lei, que é onde a varredura faz sentido.
 */
export const POR_PAGINA_PADRAO = 20;

/** Os estados do ciclo (D-43), na ordem em que o trabalho anda. */
export const ESTADOS: EstadoDoArtigo[] = [
  'pendente',
  'reivindicado',
  'sugerido',
  'publicado',
  'devolvido',
];

/**
 * O recorte, **sem o estado**.
 *
 * Separado porque é exatamente o que o resumo recebe: contar por estado é o que
 * se quer saber *antes* de escolher o estado.
 */
export interface RecorteDoInventario {
  /** A citação da lei, que é o mesmo parâmetro do backlog - o filtro atravessa as abas. */
  lei?: string;
  termo: string;
}

export interface ConsultaDoInventario extends RecorteDoInventario {
  estado?: EstadoDoArtigo;
  ordem?: OrdemDosArtigos;
  page: number;
  porPagina: number;
}

/**
 * Lê da URL a consulta inteira.
 *
 * O que vem de lá é texto que qualquer um pode digitar na barra de endereço,
 * então estado, ordem e tamanho de página passam pela lista de valores
 * conhecidos antes de virar consulta - o servidor recusaria, mas com erro em vez
 * de lista vazia.
 */
export function lerConsulta(params: URLSearchParams): ConsultaDoInventario {
  const texto = (chave: string): string | undefined => params.get(chave) ?? undefined;
  const estado = texto('estado');
  const porPagina = Number(texto('porPagina'));

  return {
    lei: texto('lei'),
    termo: texto('termo') ?? '',
    estado: ESTADOS.includes(estado as EstadoDoArtigo) ? (estado as EstadoDoArtigo) : undefined,
    ordem: lerOpcao<OrdemDosArtigos>(texto('ordem'), ORDENS),
    page: lerPagina(params),
    porPagina: TAMANHOS_DE_PAGINA.includes(porPagina) ? porPagina : POR_PAGINA_PADRAO,
  };
}

/** O que a API recebe, dos dois lados: a lista e o resumo pedem o mesmo recorte. */
const comoFiltro = (recorte: RecorteDoInventario) => ({
  lei: recorte.lei,
  termo: recorte.termo || undefined,
});

/** Chave e função de uma página do inventário. */
export function paginaDoInventario(consulta: ConsultaDoInventario) {
  const { estado, ordem, page, porPagina, ...recorte } = consulta;

  return {
    queryKey: ['commented-articles', 'pagina', { ...recorte, estado, ordem, page, porPagina }],
    queryFn: () =>
      api.listCommentedArticles({
        ...comoFiltro(recorte),
        state: estado,
        ordem,
        page,
        pageSize: porPagina,
      }),
  };
}

/**
 * Chave e função do resumo.
 *
 * **Sem o estado, sem a ordem e sem a página**, e é isso que faz o resumo
 * sobreviver a virar a página: a chave não muda, então o cache responde sem ir à
 * rede. Fosse um campo da listagem, cada clique recontaria o inventário inteiro.
 */
export function resumoDoInventario(consulta: ConsultaDoInventario) {
  const { estado: _estado, ordem: _ordem, page: _page, porPagina: _porPagina, ...recorte } = consulta;

  return {
    queryKey: ['commented-articles', 'resumo', recorte],
    queryFn: () => api.commentedArticlesSummary(comoFiltro(recorte)),
  };
}

/** As opções do filtro de estado, com quanto há em cada um. */
export function opcoesDeEstado(
  resumo: ResumoDosArtigos | undefined,
  rotulo: (estado: EstadoDoArtigo) => string,
): { label: string; value: EstadoDoArtigo | 'todos'; count?: number }[] {
  const total = resumo ? Object.values(resumo).reduce((soma, valor) => soma + valor, 0) : undefined;

  return [
    { label: 'Todos', value: 'todos', count: total },
    ...ESTADOS.map((estado) => ({
      label: rotulo(estado),
      value: estado,
      count: resumo?.[estado],
    })),
  ];
}
